import {useEffect, useMemo, useRef} from 'react';
import {useTaopix} from '../Components/Context/TaopixContext';
import {
  Common,
  CoreDoms,
  NumBool,
  PageConstants,
  SelectedSide,
  Viewers,
  ViewMode,
} from '@taopix/taopix-core';
import {useUiState} from '../Components/Context/UserInterface/UserInterfaceContext';
import {useSelector} from 'react-redux';
import {LayoutSelector} from '../Selectors';

/**
 * Hook to handle project viewer related keyboard shortcuts for UI navigation.
 */
export function useProjectViewerKeyboardCommands() {
  const taopix = useTaopix();
  const ui = useUiState();
  const productLayout = useSelector(LayoutSelector);
  const statePageNumberRef = useRef(ui.state.pageNumber);
  const app = useTaopix();

  const {columns, sortedPages} = ui.state.gridViewColumnData;

  const currentIndex = useMemo(() => {
    return sortedPages.findIndex(p => p.pagenumber === ui.state.pageNumber);
  }, [ui.state.pageNumber, sortedPages]);

  // Update the page number ref whenever it changes
  useEffect(() => {
    statePageNumberRef.current = ui.state.pageNumber;
  }, [ui.state.pageNumber]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const onCover = [
        PageConstants.FrontCoverPageNumber,
        PageConstants.BackCoverPageNumber,
      ].includes(statePageNumberRef.current);
      // Add shortcuts for left and right side selection
      if (NumBool.True === productLayout.spreads) {
        if (event.key === '<' || event.key === ',') {
          if (!onCover) {
            taopix.core
              .get('stateManager')
              .setSelectedSide(SelectedSide.Left, statePageNumberRef.current, true, true);
          }
        }
        if (event.key === '>' || event.key === '.') {
          if (!onCover) {
            taopix.core
              .get('stateManager')
              .setSelectedSide(SelectedSide.Right, statePageNumberRef.current, true, true);
          }
        }
      }

      if ([ViewMode.Single, ViewMode.Preview, ViewMode.List].includes(ui.state.viewMode)) {
        const nextPageKey = ui.state.viewMode === ViewMode.Single ? 'ArrowRight' : 'ArrowDown';
        const prevPageKey = ui.state.viewMode === ViewMode.Single ? 'ArrowLeft' : 'ArrowUp';

        if ([nextPageKey, prevPageKey].includes(event.key) && event.shiftKey) {
          const isSpreads = NumBool.True === productLayout.spreads;

          const firstPageNumber = Common.getDisplayPageNumber(
            productLayout.firstpagenumber,
            isSpreads
          );

          const lastPageNumber = Common.getDisplayPageNumber(productLayout.lastpage, isSpreads);

          let newPageNumber = statePageNumberRef.current;

          const isFrontCover = newPageNumber === PageConstants.FrontCoverPageNumber;
          const isBackCover = newPageNumber === PageConstants.BackCoverPageNumber;

          const isFirstPage = newPageNumber === firstPageNumber;
          const isLastPage = newPageNumber === lastPageNumber;

          const hasCover1 = NumBool.True === productLayout.cover.cover1active;
          const hasCover2 = NumBool.True === productLayout.cover.cover2active;

          const modifier = isSpreads ? 2 : 1;

          if (nextPageKey === event.key) {
            if (!isLastPage) {
              newPageNumber = isFrontCover ? firstPageNumber : newPageNumber + modifier;
            } else if (hasCover2) {
              newPageNumber = PageConstants.BackCoverPageNumber;
            }
          }

          if (prevPageKey === event.key) {
            if (isFirstPage && hasCover1) {
              newPageNumber = PageConstants.FrontCoverPageNumber;
            } else if (isBackCover) {
              newPageNumber = lastPageNumber;
            } else if (!isFrontCover) {
              newPageNumber -= modifier;
            }
          }

          // Scroll to next/previous page
          if (newPageNumber !== statePageNumberRef.current) {
            document
              .querySelector(`#${CoreDoms.ViewportName}-${Viewers.Primary}-${newPageNumber}`)
              ?.scrollIntoView({behavior: 'auto', block: 'center', inline: 'nearest'});
          }
        }
      } else if (
        ViewMode.Grid === ui.state.viewMode &&
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key) &&
        event.shiftKey
      ) {
        let newIndex = currentIndex;

        switch (event.key) {
          case 'ArrowRight':
            if (currentIndex + 1 < sortedPages.length) newIndex++;
            break;
          case 'ArrowLeft':
            if (currentIndex - 1 >= 0) newIndex--;
            break;
          case 'ArrowDown':
            if (currentIndex + columns < sortedPages.length) newIndex += columns;
            break;
          case 'ArrowUp':
            if (currentIndex - columns >= 0) newIndex -= columns;
            break;
          default:
            return;
        }

        if (newIndex !== currentIndex) {
          const newPageNumber = sortedPages[newIndex].pagenumber;
          app.core.get('abilities').setPage(newPageNumber).then();
        }
      }
    }

    // Add and remove the keyboard event listener
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [ui.state.viewMode, sortedPages, columns, currentIndex]);
}
